import React, { useEffect, useState, useRef } from "react"
import { Breadcrumb, TitleScreen } from "components/Core"
import cn from "classnames"
import { get_business_rules, put_business_rules } from "services/middleware_services"
import usePreRender from "hooks/usePreRender"
import { toast } from "react-hot-toast"
import GenericJsonForm from "./components/GenericJsonForm"
import Swal from "sweetalert2"
import { Wand2, FileCheck, Copy, Download, Upload, RotateCcw } from "lucide-react"
import JsonCodeEditor from "./components/JsonCodeEditor"
import useAuthorizer from "overrides/theme/entel/base/hooks/useAuthorizer"

const showAuthErrorModal = (onLogout: () => void) => {
  Swal.fire({
    html: `
      <div class="flex flex-col items-center justify-center pt-4 pb-1">
        <div class="w-12 h-12 bg-yellow-50 rounded-full flex items-center justify-center mb-3">
          <svg class="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
        </div>
        <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">Sesión expirada</h3>
        <p class="text-[13px] text-gray-500 font-medium leading-snug px-2 text-center">No tienes permisos o tu sesión ha expirado. Por favor vuelve a iniciar sesión.</p>
      </div>
    `,
    showConfirmButton: true,
    confirmButtonText: "Ir al Login",
    buttonsStyling: false,
    allowOutsideClick: false,
    customClass: {
      popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
      actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
      confirmButton: "!w-full !bg-transparent !text-[#FF9500] !text-[16px] !font-semibold !py-3 m-0 hover:!bg-yellow-50 transition-colors !outline-none focus:!outline-none focus:!ring-0 !shadow-none"
    },
  }).then(() => onLogout())
}

const BusinessRulesScreen = () => {
  const { _color } = usePreRender()
  const { logout_user_action } = useAuthorizer()
  const [rules, setRules] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<string>("brokering")
  const [isEditing, setIsEditing] = useState(false)
  const [editableContent, setEditableContent] = useState("")
  const [originalContent, setOriginalContent] = useState("")
  const [editMode, setEditMode] = useState<"json" | "form">("form")
  const [editableTabs, setEditableTabs] = useState<string[]>(["shippingCost"])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isDirty = isEditing && editableContent !== originalContent

  useEffect(() => {
    const fetchRules = async () => {
      try {
        setIsLoading(true)
        const response = await get_business_rules()
        const data = response?.data?.message || response?.data
        if (data) {
          setRules(data)
          
          const ignoreKeys = ["_omnixMetadata", "id", "ownerId"]
          const keys = Object.keys(data).filter(k => !ignoreKeys.includes(k))
          
          if (keys.length > 0) {
            setActiveTab(prev => keys.includes(prev) ? prev : keys[0])
          }
        }
      } catch (error: any) {
        console.error("Error fetching business rules:", error)
        const status = error?.response?.status
        if (status === 401 || status === 403) {
          showAuthErrorModal(logout_user_action)
        } else {
          toast.error("Error al obtener las reglas de negocio")
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchRules()
  }, [logout_user_action])

  const ignoreKeys = ["_omnixMetadata", "id", "ownerId"]
  
  const tabs = rules
    ? Object.keys(rules)
        .filter((key) => !ignoreKeys.includes(key))
        .map((key) => {
          const labels: Record<string, string> = {
            brokering: "Brokering",
            channels: "Canales",
            core: "Core",
            integrations: "Integraciones",
            cancelReasons: "Razones de Cancelación",
            regionMapping: "Mapeo de Regiones",
            createOrderInputSchemaValidation: "Validación de Esquema",
            shippingCost: "Costo de Envío",
          }
          return { id: key, label: labels[key] || key }
        })
    : []

  const activeData = rules ? rules[activeTab] : null

  useEffect(() => {
    setIsEditing(false)
    if (activeData) {
      const s = JSON.stringify(activeData, null, 2)
      setEditableContent(s)
      setOriginalContent(s)
    } else {
      setEditableContent("")
      setOriginalContent("")
    }
  }, [activeTab, activeData])

  const handleSave = async () => {
    try {
      const parsedData = JSON.parse(editableContent)
      setIsLoading(true)
      const payload = {
        [activeTab]: parsedData
      }
      await put_business_rules(payload)
      toast.success("Reglas actualizadas correctamente")
      setRules((prev: any) => ({ ...prev, [activeTab]: parsedData }))
      const s = JSON.stringify(parsedData, null, 2)
      setOriginalContent(s)
      setIsEditing(false)
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        toast.error("Error de sintaxis JSON. Verifica el formato antes de guardar.")
      } else {
        const status = err?.response?.status
        if (status === 401 || status === 403) {
          showAuthErrorModal(logout_user_action)
        } else {
          toast.error("Error al actualizar las reglas")
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(editableContent)
      setEditableContent(JSON.stringify(parsed, null, 2))
      toast.success("JSON formateado correctamente")
    } catch {
      toast.error("No se puede formatear: JSON inválido")
    }
  }

  const handleValidateJson = () => {
    try {
      JSON.parse(editableContent)
      toast.success("✓ JSON válido")
    } catch (e: any) {
      // Extract character position from error message (e.g. "at position 42")
      const posMatch = e.message?.match(/at position (\d+)/)
      const lineColMatch = e.message?.match(/at line (\d+) column (\d+)/)

      if (lineColMatch) {
        const line = lineColMatch[1]
        const col = lineColMatch[2]
        toast.error(`Error en línea ${line}, columna ${col}`, { duration: 5000 })
      } else if (posMatch) {
        const pos = parseInt(posMatch[1], 10)
        const before = editableContent.slice(0, pos)
        const line = before.split("\n").length
        const col = pos - before.lastIndexOf("\n")
        toast.error(`Error en línea ${line}, columna ${col}`, { duration: 5000 })
      } else {
        toast.error(`JSON Inválido: ${e.message}`, { duration: 5000 })
      }
    }
  }

  const handleCopyJson = () => {
    navigator.clipboard.writeText(editableContent)
    toast.success("JSON copiado al portapapeles")
  }

  const handleReset = () => {
    setEditableContent(originalContent)
    toast.success("Cambios descartados")
  }

  const handleExport = () => {
    const blob = new Blob([editableContent], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${activeTab}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success("Archivo exportado")
  }

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      try {
        JSON.parse(text)
        setEditableContent(text)
        toast.success(`"${file.name}" importado correctamente`)
      } catch {
        toast.error("El archivo no es un JSON válido")
      }
    }
    reader.readAsText(file)
    e.target.value = ""
  }

  const handleCreateRule = async () => {
    const { value: ruleName } = await Swal.fire({
      title: "Nueva Regla de Negocio",
      input: "text",
      inputLabel: "Nombre de la regla (sin espacios, usar camelCase)",
      inputPlaceholder: "ej. customPricing",
      showCancelButton: true,
      confirmButtonText: "Crear",
      cancelButtonText: "Cancelar",
      inputValidator: (value) => {
        if (!value) return "Debes ingresar un nombre"
        if (Object.keys(rules || {}).includes(value)) return "Ya existe una regla con este nombre"
        if (/\s/.test(value)) return "El nombre no puede contener espacios"
      },
    })

    if (ruleName) {
      try {
        setIsLoading(true)
        const payload = {
          [ruleName]: {},
        }
        await put_business_rules(payload)
        toast.success("Nueva regla creada correctamente")

        setRules((prev: any) => ({ ...prev, [ruleName]: {} }))
        setEditableTabs((prev) => [...prev, ruleName])
        setActiveTab(ruleName)
        setEditableContent("{}")
        setIsEditing(true)
        setEditMode("json")
      } catch (err) {
        toast.error("Error al crear la regla")
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-start bg-gray-50/50">
      <Breadcrumb
        data={[
          { name: "Configuración", href: "/settings" },
          { name: "Reglas de negocio", href: "/settings/business-rules" },
        ]}
      />
      <div className="w-full flex flex-col justify-center items-start py-4 px-6 md:px-10">
        <div className="w-full flex justify-between items-center">
          <TitleScreen title="Reglas de Negocio" />
          <div className="flex items-center gap-4">
            {rules?._omnixMetadata?.updatedAt && (
              <div className="flex items-center gap-2.5 px-4 py-2 bg-white border border-gray-100 rounded-full shadow-sm transition-all hover:shadow-md">
                <div className="relative flex h-2 w-2">
                  <span className={cn("animate-ping absolute inline-flex h-full w-full rounded-full opacity-75", `bg_${_color}`)}></span>
                  <span className={cn("relative inline-flex rounded-full h-2 w-2", `bg_${_color}`)}></span>
                </div>
                <span className="text-xs font-medium text-gray-500">
                  Actualizado el{" "}
                  <strong className="text-gray-800 font-semibold ml-0.5">
                    {new Date(Number(rules._omnixMetadata.updatedAt)).toLocaleString("es-ES", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </strong>
                </span>
              </div>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Visualiza las configuraciones, integraciones y parámetros de negocio actuales.
        </p>
      </div>

      <div className="w-full flex-1 flex flex-col lg:flex-row pb-6 px-4 md:px-10 gap-6">
        {/* Sidebar / Tabs */}
        <div className="w-full lg:w-64 xl:w-72 shrink-0 flex flex-col gap-2">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              onClick={async () => {
                if (isDirty && activeTab !== tab.id) {
                  const res = await Swal.fire({
                    html: `<div class="flex flex-col items-center justify-center pt-4 pb-1"><div class="w-12 h-12 bg-yellow-50 rounded-full flex items-center justify-center mb-3"><svg class="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg></div><h3 class="text-[17px] font-semibold text-gray-900 mb-1">Cambios sin guardar</h3><p class="text-[13px] text-gray-500 px-2 text-center">Tienes cambios sin guardar en esta sección. ¿Deseas descartarlos?</p></div>`,
                    showCancelButton: true,
                    confirmButtonText: "Descartar",
                    cancelButtonText: "Quedarme",
                    buttonsStyling: false,
                    customClass: {
                      popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
                      actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
                      confirmButton: "!w-1/2 !bg-transparent !text-[#F9004D] !text-[16px] !font-semibold !py-3 m-0 !border-l !border-gray-200/60 hover:!bg-red-50 !outline-none focus:!ring-0",
                      cancelButton: "!w-1/2 !bg-transparent !text-[#007AFF] !text-[16px] !font-normal !py-3 m-0 hover:!bg-gray-50 !outline-none focus:!ring-0",
                    }
                  })
                  if (!res.isConfirmed) return
                }
                setActiveTab(tab.id)
              }}
              className={cn(
                "w-full flex items-center justify-between p-3 lg:p-4 text-sm font-semibold border bg-white rounded-xl shadow-sm cursor-pointer transition-all duration-200",
                `hover:border_${_color} hover:text_${_color}`,
                {
                  [`border_${_color} text_${_color} shadow-md`]: activeTab === tab.id,
                  "border-gray-200 text-gray-600": activeTab !== tab.id,
                }
              )}
            >
              <span>{tab.label}</span>
              {isDirty && activeTab === tab.id && (
                <span className="w-2 h-2 rounded-full bg-yellow-400 shrink-0" title="Cambios sin guardar" />
              )}
            </div>
          ))}
          <button
            onClick={handleCreateRule}
            className={cn("w-full mt-2 flex items-center justify-center p-3 lg:p-4 text-sm font-bold text-white rounded-xl shadow-sm transition-all hover:opacity-90 hover:shadow-md", `bg_${_color}`)}
          >
            + Nueva Regla
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col min-h-[400px] overflow-hidden">
          <div className="p-4 md:p-6 border-b border-gray-100 flex flex-wrap justify-between items-center gap-4 bg-gray-50/50 rounded-t-xl">
            <h3 className="text-lg font-bold text-gray-800 capitalize break-all">
              {tabs.find((t) => t.id === activeTab)?.label || activeTab}
            </h3>
            {editableTabs.includes(activeTab) && !isEditing && (
              <button
                type="button"
                onClick={() => {
                  setIsEditing(true)
                  setEditMode("form")
                }}
                className={cn("px-4 py-1.5 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity", `bg_${_color}`)}
              >
                Editar
              </button>
            )}
            {editableTabs.includes(activeTab) && isEditing && (
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleReset} title="Resetear al valor original" className="p-1.5 text-gray-500 hover:text-yellow-600 bg-gray-100 rounded-md hover:bg-yellow-50 transition-colors">
                  <RotateCcw size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false)
                    setEditableContent(originalContent)
                  }}
                  className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className={cn("flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity", `bg_${_color}`)}
                >
                  {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-yellow-300" />}
                  Guardar
                </button>
              </div>
            )}
          </div>
          
          <div className={cn("flex-1 overflow-auto rounded-b-xl", editMode === "json" ? "bg-[#1E1E1E]" : "bg-white")}>
            {isLoading ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-gray-400">
                <div className="w-8 h-8 border-4 border-gray-600 border-t-blue-500 rounded-full animate-spin" />
                <p className="text-sm font-medium animate-pulse">Cargando reglas de negocio...</p>
              </div>
            ) : !rules ? (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <p className="text-sm font-medium">No se encontró información de reglas de negocio.</p>
              </div>
            ) : isEditing ? (
              <div className="w-full h-full flex flex-col min-h-[400px]">
                <div className={cn("flex flex-wrap items-center justify-between p-2 gap-2 border-b", editMode === "json" ? "bg-[#2D2D2D] border-gray-700" : "bg-gray-50 border-gray-200")}>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        if (editMode === "json") {
                          try {
                            JSON.parse(editableContent)
                            setEditMode("form")
                          } catch {
                            toast.error("Formato JSON inválido. Corrige el texto para usar el formulario.")
                          }
                        }
                      }}
                      className={cn("px-3 py-1.5 text-xs font-semibold rounded-md transition-colors",
                        editMode === "form"
                          ? `bg_${_color} text-white`
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      )}
                    >
                      Vista Formulario
                    </button>
                    <button
                      onClick={() => setEditMode("json")}
                      className={cn("px-3 py-1.5 text-xs font-semibold rounded-md transition-colors",
                        editMode === "json"
                          ? `bg_${_color} text-white`
                          : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      )}
                    >
                      Vista JSON
                    </button>
                  </div>
                  {editMode === "json" && (
                    <div className="flex items-center gap-2">
                      <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleImport} />
                      <button onClick={() => fileInputRef.current?.click()} title="Importar JSON" className="p-1.5 text-gray-400 bg-gray-700/50 rounded-md hover:bg-gray-600 hover:text-white transition-colors">
                        <Upload size={16} />
                      </button>
                      <button onClick={handleExport} title="Exportar JSON" className="p-1.5 text-gray-400 bg-gray-700/50 rounded-md hover:bg-gray-600 hover:text-white transition-colors">
                        <Download size={16} />
                      </button>
                      <button onClick={handleFormatJson} title="Formatear JSON" className="p-1.5 text-gray-400 bg-gray-700/50 rounded-md hover:bg-gray-600 hover:text-white transition-colors">
                        <Wand2 size={16} />
                      </button>
                      <button onClick={handleValidateJson} title="Validar JSON" className="p-1.5 text-gray-400 bg-gray-700/50 rounded-md hover:bg-gray-600 hover:text-white transition-colors">
                        <FileCheck size={16} />
                      </button>
                      <button onClick={handleCopyJson} title="Copiar JSON" className="p-1.5 text-gray-400 bg-gray-700/50 rounded-md hover:bg-gray-600 hover:text-white transition-colors">
                        <Copy size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <div className={cn("flex-1 overflow-auto", editMode === "json" ? "bg-[#1E1E1E]" : "bg-white")}>
                  {editMode === "json" ? (
                    <JsonCodeEditor
                      value={editableContent}
                      onChange={setEditableContent}
                      minHeight="400px"
                    />
                  ) : (
                    <GenericJsonForm jsonString={editableContent} onChangeString={setEditableContent} />
                  )}
                </div>
              </div>
            ) : activeData ? (
              <div className="w-full h-full flex flex-col min-h-[400px]">
                <div className={cn("flex flex-wrap items-center justify-between p-2 gap-2 border-b", editMode === "json" ? "bg-[#2D2D2D] border-gray-700" : "bg-gray-50 border-gray-200")}>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setEditMode("form")}
                      className={cn("px-3 py-1.5 text-xs font-semibold rounded-md transition-colors",
                        editMode === "form"
                          ? `bg_${_color} text-white`
                          : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      )}
                    >
                      Vista Formulario
                    </button>
                    <button
                      onClick={() => setEditMode("json")}
                      className={cn("px-3 py-1.5 text-xs font-semibold rounded-md transition-colors",
                        editMode === "json"
                          ? `bg_${_color} text-white`
                          : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      )}
                    >
                      Vista JSON
                    </button>
                  </div>
                  {editMode === "json" && (
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(JSON.stringify(activeData, null, 2))
                          toast.success("JSON copiado al portapapeles")
                        }} 
                        title="Copiar JSON" 
                        className="p-1.5 text-gray-400 bg-gray-700/50 rounded-md hover:bg-gray-600 hover:text-white transition-colors"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <div className={cn("flex-1 overflow-auto", editMode === "json" ? "bg-[#1E1E1E]" : "bg-white")}>
                  {editMode === "json" ? (
                    <JsonCodeEditor
                      value={JSON.stringify(activeData, null, 2)}
                      readOnly
                      minHeight="400px"
                    />
                  ) : (
                    <GenericJsonForm jsonString={JSON.stringify(activeData, null, 2)} onChangeString={() => {}} readOnly={true} />
                  )}
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <p className="text-sm font-medium italic">No hay datos configurados para esta sección.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BusinessRulesScreen
